import React from "react";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { BsArrowUpShort } from "react-icons/bs";
import { AiFillEye } from "react-icons/ai";
import { auditLogSorting } from "utils/auditLogs";
import {
  formatAction,
  formatRole,
  formatResult,
  formatResourceType,
} from "utils/auditLogs";

const AuditLogTable = ({ logs, page, sort, setSort, handleView }) => {
  const { t } = useTranslation();

  const handleSortItem = (sortByValue, sortTypeValue) => {
    const newSortType = sort.sortType === "asc" ? "desc" : "asc";
    setSort({
      sortBy: sortByValue,
      sortType: sortByValue === sort.sortBy ? newSortType : sortTypeValue,
    });
  };

  const getBadgeClass = (result) => {
    switch (result) {
      case "SUCCESS":
        return "bg-green-100 text-green-800";
      case "DENIED":
        return "bg-red-100 text-red-800";
      case "FAILURE":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCompanyId = (company) => {
    if (!company) return null;
    if (typeof company === "object") return company.id || null;
    return company;
  };

  const getSimpleRole = (item) => {
    const role = item?.actor?.role || item?.actorRole;

    if (role) {
      if (role.toLowerCase() === "hr") return "HR";
      if (role.toLowerCase() === "admin") return "Admin";
      if (role.toLowerCase() === "accountant") return "Accountant";
      if (role.toLowerCase() === "employee") return "Employee";
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    }

    const companyId = getCompanyId(item.companyId);
    if (!companyId && item.action === "EMPLOYEE_LOGIN") return "Candidate";
    return null;
  };

  const getFormattedAction = (item) => {
    const companyId = getCompanyId(item.companyId);
    const formattedAction = formatAction(item.action) || "";

    if (!companyId && formattedAction.toLowerCase().includes("employee")) {
      return formattedAction.replace(/employee/gi, "Candidate");
    }
    return formattedAction;
  };

  const getFormattedResourceType = (item) => {
    const companyId = getCompanyId(item.companyId);

    if (!companyId && item?.resourceType === "employee") {
      return "Candidate";
    }
    return formatResourceType(item?.resourceType);
  };

  return (
    <div className="p-3">
      <div className="overflow-x-auto relative rounded-lg border">
        <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="py-3 px-6">
                {t("SR_NO")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("ACTOR")}
              </th>
              {auditLogSorting.map((option) => (
                <th
                  className="py-3 cursor-pointer px-6"
                  onClick={() => handleSortItem(option.sortBy, "asc")}
                  key={option.sortBy}
                  scope="col"
                >
                  <div className="justify-start flex">
                    <span>{t(option.displayText)}</span>
                    <span>
                      {sort.sortBy === option.sortBy &&
                        sort.sortType === "asc" && (
                          <BsArrowUpShort className="h-4 w-4" />
                        )}
                      {sort.sortBy === option.sortBy &&
                        sort.sortType === "desc" && (
                          <BsArrowUpShort className="h-4 rotate-180 w-4" />
                        )}
                    </span>
                  </div>
                </th>
              ))}
              <th scope="col" className="py-3 px-6">
                {t("O_ACTION")}
              </th>
            </tr>
          </thead>
          <tbody>
            {logs &&
              logs?.map((item, i) => {
                let displayResourceId =
                  item?.resourceId && item.resourceId !== "undefined"
                    ? String(item.resourceId)
                    : null;

                // ✅ ADDED: Trim long resource ID (like JWT tokens in PIPEDRIVE_CALLBACK)
                if (displayResourceId && displayResourceId.length > 30) {
                  displayResourceId = `${displayResourceId.substring(0, 30)}...`;
                }

                const role = getSimpleRole(item);
                const actorName =
                  item?.actorDisplayName || displayResourceId || "-";
                const companyName = item?.companyDisplayName;

                return (
                  <tr
                    key={item._id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <th
                      scope="row"
                      className="py-4 px-6 border-r font-medium text-gray-900 dark:text-white"
                    >
                      {i + 1 + 10 * (page - 1)}
                    </th>
                    <td className="py-4 px-6 border-r text-gray-900">
                      <div className="text-left">
                        {/* Added a max-width and truncate class just in case */}
                        <span className="font-medium block max-w-[300px] truncate" title={item?.actorDisplayName || displayResourceId}>
                          {role
                            ? `${formatRole(role)} - ${actorName}`
                            : actorName}
                        </span>
                        {companyName && (
                          <span className="block text-gray-500 text-[10px] mt-1">
                            {companyName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left font-medium">
                      {getFormattedAction(item)}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getFormattedResourceType(item)}
                      </span>
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      {item?.createdAt
                        ? dayjs(item?.createdAt).format("MM-DD-YYYY hh:mm A")
                        : "-"}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBadgeClass(item?.result)}`}
                      >
                        {formatResult(item?.result)}
                      </span>
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-center">
                      <ul className="flex justify-center">
                        <li
                          onClick={() => handleView(item)}
                          className="px-2 py-2 hover:bg-white hover:text-LightBlue cursor-pointer"
                        >
                          <a title="View audit log">
                            <AiFillEye className="cursor-pointer w-5 h-5 text-slate-600" />
                          </a>
                        </li>
                      </ul>
                    </td>
                  </tr>
                );
              })}
            {isEmpty(logs) ? (
              <tr className="bg-white text-center border-b dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6 border-r" colSpan={10}>
                  {t("O_NO_RECORD_FOUND")}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLogTable;